/**
 * The itemised review a customer reads immediately before committing.
 *
 * # Chapter 10 (F10 + F11)
 *
 * Two testers reported the same defect from opposite ends. One was surprised
 * that the button on the right sent the request immediately and wanted to see a
 * preview first. The other described the scroll dance the page's three competing
 * CTAs forced. Both are answered by the same change: one primary action on the
 * page opens this, and the only thing that sends is the button in here.
 *
 * # What it must show, and why
 *
 * Everything a client would want to check before committing money: the box, the
 * quantity and the band it prices at, every selection with its own unit price
 * and line total, the order total, the delivery date, the logistics choices, the
 * logo filename and the contact details. The last thing a customer reads before
 * committing is the wrong place to be terse — but it is also the wrong place for
 * a wall of text, so it is three labelled sections with a "Change" on each and
 * money right-aligned in a column the eye can run down.
 *
 * # The rule that makes it a review rather than a slower submit button
 *
 * It must be possible to go back and change something WITHOUT losing what has
 * been entered. This component therefore holds no form state at all: it reads
 * what it is given, and closing it returns the customer to a quote page whose
 * every field is exactly as they left it, because the state never lived here.
 */

import React from 'react';
import { motion } from 'motion/react';
import { AlertCircle, Check, ChevronLeft, Send, X } from 'lucide-react';
import { formatMoney } from '@brandbox/content-schema';
import type { CustomerInfo } from '../../types';
import type { ReviewLine } from '../../lib/reviewSummary';

/** The section a "Change" button sends the customer back to. */
export type ReviewSection = 'box' | 'details' | 'contact';

export interface ReviewOrder {
  boxName: string;
  qty: number;
  /** Human band range, e.g. "250-499". `null` below the lowest band floor. */
  bandLabel: string | null;
  lines: readonly ReviewLine[];
  unitPrice: number;
  total: number;
  currency: string;
  vatSuffix: string;
  deliveryDate: string;
  storage: string;
  shipment: string;
  notes: string;
  /**
   * Filenames of the attached logos. An array from the start: F15 (multi-logo)
   * lands in P4 and this surface must not need reshaping for it.
   */
  logoFilenames: readonly string[];
  contact: CustomerInfo;
}

export interface ReviewState {
  isSubmitting: boolean;
  /** After a recoverable logo failure the send label must say what it now does. */
  logoUploadFailed: boolean;
  error: string | null;
}

interface Props {
  open: boolean;
  order: ReviewOrder;
  state: ReviewState;
  /**
   * Chapter 10 (F23) — the authored label for the button that actually sends.
   * The failure variants below stay derived: they depend on runtime state, not
   * on authored copy.
   */
  sendLabel: string;
  onClose: () => void;
  onSend: () => void;
  onEdit: (section: ReviewSection) => void;
}

function SectionHeader({
  title,
  onChange,
}: {
  title: string;
  onChange: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-gray-100 pb-2">
      <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-gray-400">{title}</h4>
      <button
        type="button"
        onClick={onChange}
        className="text-[11px] font-black uppercase tracking-widest text-[#356d4e] underline underline-offset-4 hover:text-[#57c858] transition-colors"
      >
        Change
      </button>
    </div>
  );
}

function Row({
  label,
  children,
  testId,
}: {
  label: string;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <div
      {...(testId ? { 'data-testid': testId } : {})}
      className="flex items-baseline justify-between gap-6 py-1.5"
    >
      <span className="text-xs font-bold text-gray-400">{label}</span>
      <span className="text-sm font-bold text-[#444444] text-right break-words min-w-0">
        {children}
      </span>
    </div>
  );
}

export function ReviewRequestModal({
  open,
  order,
  state,
  sendLabel: authoredSendLabel,
  onClose,
  onSend,
  onEdit,
}: Props) {
  const titleId = React.useId();
  const dialogRef = React.useRef<HTMLDivElement>(null);

  // Escape closes without sending, and the dialog takes focus on open so a
  // keyboard user is not left behind on the page underneath.
  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    dialogRef.current?.focus?.();
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const { contact } = order;
  // F15 — after a failed upload the label has to say what it now does, and with
  // several files attached that is "logos". The singular copy is preserved
  // exactly for the one-logo case.
  const sendLabel = state.isSubmitting
    ? 'Sending…'
    : state.logoUploadFailed
      ? order.logoFilenames.length > 1
        ? 'Send without logos'
        : 'Send without logo'
      : authoredSendLabel;

  return (
    <div className="fixed inset-0 z-[130] flex items-start justify-center overflow-y-auto p-4 sm:p-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-md"
        onClick={onClose}
      />
      <motion.div
        ref={dialogRef}
        tabIndex={-1}
        initial={{ opacity: 0, scale: 0.96, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative my-4 w-full max-w-2xl rounded-[32px] bg-white p-6 shadow-2xl outline-none sm:p-10"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close review"
          className="absolute right-5 top-5 text-gray-300 transition-colors hover:text-[#444444]"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mb-8 space-y-2 pr-8">
          <h3
            id={titleId}
            className="text-2xl font-black uppercase tracking-tight text-[#444444] sm:text-3xl"
          >
            Check your request
          </h3>
          {/* The sentence that answers F11 directly: the customer was surprised
              by an immediate send, so the review states its own contract. */}
          <p className="text-sm font-bold text-[#356d4e]">
            Nothing is sent until you press &ldquo;{sendLabel}&rdquo;.
          </p>
        </div>

        <div className="space-y-8">
          <section className="space-y-3">
            <SectionHeader title="Your box" onChange={() => onEdit('box')} />
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-lg font-black uppercase tracking-tight text-[#356d4e]">
                {order.boxName}
              </span>
              <span className="text-sm font-bold text-gray-400">{order.qty} boxes</span>
              {order.bandLabel !== null && (
                <span className="rounded-md border border-[#57c858]/30 bg-[#57c858]/10 px-2 py-0.5 text-[11px] font-black uppercase tracking-widest text-[#356d4e]">
                  {order.bandLabel} tier
                </span>
              )}
            </div>

            <ul className="divide-y divide-gray-100">
              {order.lines.map((line) => (
                <li
                  key={line.id}
                  data-testid={`review-line-${line.id}`}
                  className="flex items-baseline justify-between gap-4 py-2"
                >
                  <span className="min-w-0">
                    <span
                      className={`block text-sm font-bold ${
                        line.unavailable ? 'text-gray-400' : 'text-[#444444]'
                      }`}
                    >
                      {line.label}
                    </span>
                    <span className="block text-[11px] font-bold uppercase tracking-widest text-gray-300">
                      {line.stepLabel}
                      {line.unavailable && (
                        <span className="ml-2 text-red-500">
                          Min. {line.minBoxes} boxes — not included
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="flex-shrink-0 text-right">
                    {line.unavailable ? (
                      <span className="text-sm font-black text-gray-300">—</span>
                    ) : (
                      <>
                        <span className="block text-sm font-black text-[#356d4e]">
                          {formatMoney(line.lineTotal, order.currency)}
                        </span>
                        <span className="block text-[11px] font-bold text-gray-400">
                          {formatMoney(line.unitPrice, order.currency)} / box
                        </span>
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>

            <div className="rounded-2xl bg-gray-50 px-5 py-4">
              <Row label="Price per box" testId="review-unit-price">
                {formatMoney(order.unitPrice, order.currency)}
              </Row>
              <div
                data-testid="review-total"
                className="flex items-baseline justify-between gap-6 border-t border-gray-200 pt-2"
              >
                <span className="text-[11px] font-black uppercase tracking-widest text-gray-400">
                  Estimated total
                </span>
                <span className="flex items-baseline gap-2">
                  <span className="text-2xl font-black text-[#356d4e]">
                    {formatMoney(order.total, order.currency)}
                  </span>
                  <span className="text-[11px] font-bold text-gray-400">{order.vatSuffix}</span>
                </span>
              </div>
            </div>
          </section>

          <section className="space-y-1">
            <SectionHeader title="Delivery & logistics" onChange={() => onEdit('details')} />
            {order.deliveryDate && (
              <Row label="Delivery date" testId="review-delivery">
                {order.deliveryDate}
              </Row>
            )}
            <Row label="Storage">{order.storage}</Row>
            <Row label="Shipment">{order.shipment}</Row>
            <Row label="Logo" testId="review-logo">
              {order.logoFilenames.length > 0 ? (
                <span className="flex flex-col items-end gap-0.5">
                  {order.logoFilenames.map((name) => (
                    <span key={name}>{name}</span>
                  ))}
                </span>
              ) : (
                <span className="text-gray-400">No logo attached</span>
              )}
            </Row>
            {order.notes && <Row label="Notes">{order.notes}</Row>}
          </section>

          <section className="space-y-1">
            <SectionHeader title="Contact" onChange={() => onEdit('contact')} />
            <Row label="Company">{contact.company}</Row>
            <Row label="Name">{contact.fullName}</Row>
            <Row label="Email">{contact.email}</Row>
            {contact.phone && <Row label="Phone">{contact.phone}</Row>}
          </section>
        </div>

        {state.error && (
          <div className="mt-8 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
            <p className="text-xs font-bold text-red-700">{state.error}</p>
          </div>
        )}

        <div className="mt-8 flex flex-col gap-3 border-t border-gray-100 pt-6 sm:flex-row-reverse">
          <button
            type="button"
            onClick={onSend}
            disabled={state.isSubmitting}
            className="flex flex-1 items-center justify-center gap-2 rounded-[20px] bg-linear-to-r from-[#356d4e] to-[#57c858] px-8 py-4 text-sm font-black uppercase tracking-widest text-white shadow-lg shadow-[#356d4e]/20 transition-all hover:brightness-110 active:scale-[0.99] disabled:opacity-50 disabled:grayscale"
          >
            {state.isSubmitting ? (
              <Check className="h-4 w-4 animate-pulse" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {sendLabel}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center gap-1.5 rounded-[20px] border border-gray-200 px-6 py-4 text-sm font-bold uppercase tracking-widest text-[#444444] transition-colors hover:bg-gray-50"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to editing
          </button>
        </div>
      </motion.div>
    </div>
  );
}
